import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@monksflow/monks-ui';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import { EmptyStateTitle } from '@/shared/ui';
import {
  MEDIA_CHANNEL_LABELS,
  SERVING_STATUS_LABELS,
  projectCampaignsQueryOptions,
  useUnlinkCampaign,
  useUpdateCampaignRole,
  type ProjectCampaign,
  type CampaignRole,
} from '@/entities/media-campaign';
import { connectorPlatformLabel } from '@/entities/media-connector';
import { AddCampaignDialog } from '@/features/performance-campaign-link';
import { DeliveryFileUploadDialog } from '@/features/delivery-file-upload';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { track } from '@/shared/analytics';
import { notificationService } from '@/lib/services/notification.service';
import { formatShortDate } from '@/shared/utils/format';

/** Widened to a plain string index so an unrecognised value falls back to itself. */
const CHANNEL_LABELS: Record<string, string> = MEDIA_CHANNEL_LABELS;
const SERVING_LABELS: Record<string, string> = SERVING_STATUS_LABELS;

/**
 * Performance → Manage campaigns.
 *
 * Where someone says which campaigns the Overview reports, and what each one is: the
 * role in particular, because it decides whose impressions the headline counts and a
 * change that large must not be invisible. Loading, empty and error are kept apart the
 * same way the Overview tab keeps them - a failed load must never read as a project
 * with nothing linked.
 */
export function ManageCampaigns() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projectId = project.platformProjectId;

  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));
  const unlink = useUnlinkCampaign(projectId);
  const updateRole = useUpdateCampaignRole(projectId);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const canUploadDelivery = useBooleanFlagValue('data_ingestion', false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Held rather than removed on the spot: unlinking stops reporting a live campaign, and
  // a row action alone does not say that the platform itself survives.
  const [toRemove, setToRemove] = useState<ProjectCampaign | null>(null);

  const rows = campaigns.data ?? [];

  const handleRoleChange = (campaign: ProjectCampaign, next: string) => {
    const role = next as CampaignRole;
    if (role === campaign.role) return;

    updateRole.mutate(
      { campaignId: campaign.id, role, counterpartCampaignId: campaign.counterpartCampaignId },
      {
        onSuccess: () =>
          track('performance_campaign_role_changed', {
            project_id: projectId,
            campaign_id: campaign.id,
            role,
          }),
        onError: () =>
          notificationService.showError(t('projectDetail.manageCampaigns.couldNotChangeThatRole')),
      },
    );
  };

  const handleConfirmRemove = () => {
    if (!toRemove) return;
    const removed = toRemove;
    unlink.mutate(removed.id, {
      onSuccess: () => {
        track('performance_campaign_unlinked', {
          project_id: projectId,
          campaign_id: removed.id,
          // The origin, as before manual campaigns had a platform: a null connectorId is
          // manual, a set one whose platform didn't resolve is unknown, not manual.
          // eslint-disable-next-line i18next/no-literal-string -- analytics enums, not copy
          platform: removed.synced ? (removed.platform ?? 'unknown') : 'manual',
        });
        setToRemove(null);
      },
      onError: () =>
        notificationService.showError(
          t('projectDetail.manageCampaigns.couldNotRemoveThatCampaign'),
        ),
    });
  };

  // One crossfade for the whole answer, so the table and the invitation to add
  // a campaign never dissolve into an identical copy of themselves.
  const viewState = campaigns.isError
    ? SETTLED_STATE.error
    : campaigns.isLoading
      ? SETTLED_STATE.loading
      : rows.length === 0
        ? SETTLED_STATE.empty
        : SETTLED_STATE.rows;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('projectDetail.manageCampaigns.everyCampaignHereContributes')}
        </p>
        <div className="flex items-center gap-sm">
          {canUploadDelivery && (
            <Button variant="outline" onClick={() => setIsUploadOpen(true)}>
              {t('projectDetail.manageCampaigns.uploadDelivery')}
            </Button>
          )}
          <Button onClick={() => setIsAddOpen(true)}>
            {t('projectDetail.manageCampaigns.addCampaign')}
          </Button>
        </div>
      </div>

      <SettledContent
        state={viewState}
        className="min-h-0 flex-1"
        contentClassName="flex min-h-0 flex-1 flex-col gap-4"
      >
        {campaigns.isError ? (
          <Empty role="alert" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.manageCampaigns.couldNotLoadThisProjectsCampaigns')}
              </EmptyStateTitle>
              <EmptyDescription>
                {campaigns.error instanceof Error
                  ? campaigns.error.message
                  : t('projectDetail.manageCampaigns.theRequestDidNotComeBack')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => void campaigns.refetch()}>
                {t('projectDetail.manageCampaigns.tryAgain')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : campaigns.isLoading ? (
          <Skeleton className="h-40 w-full" data-testid="campaigns-skeleton" />
        ) : rows.length === 0 ? (
          <Empty className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>{t('projectDetail.manageCampaigns.noCampaignsYet')}</EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.manageCampaigns.linkACampaignFromAConnectedAccount')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Datatable data-testid="campaigns-table">
            <DatatableHeader>
              <DatatableRow>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.campaign')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.platform')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.channel')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.serving')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.flight')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('projectDetail.manageCampaigns.currency')}
                </DatatableHead>
                <DatatableHead scope="col">{t('projectDetail.manageCampaigns.role')}</DatatableHead>
                <DatatableHead scope="col">
                  <span className="sr-only">{t('projectDetail.manageCampaigns.actions')}</span>
                </DatatableHead>
              </DatatableRow>
            </DatatableHeader>
            <DatatableBody>
              {rows.map((campaign) => (
                <DatatableRow key={campaign.id}>
                  <DatatableCell>{campaign.name}</DatatableCell>
                  <DatatableCell>
                    <span className="flex items-center gap-xs">
                      {campaign.platform ? connectorPlatformLabel(campaign.platform) : '—'}
                      {!campaign.synced && (
                        <Badge variant="outline" size="sm">
                          {t('projectDetail.manageCampaigns.manual')}
                        </Badge>
                      )}
                    </span>
                  </DatatableCell>
                  <DatatableCell>
                    {campaign.channel === null
                      ? '—'
                      : (CHANNEL_LABELS[campaign.channel] ?? campaign.channel)}
                  </DatatableCell>
                  <DatatableCell>
                    <Badge>
                      {SERVING_LABELS[campaign.servingStatus] ?? campaign.servingStatus}
                    </Badge>
                  </DatatableCell>
                  <DatatableCell>
                    {campaign.flightStartDate
                      ? `${formatShortDate(campaign.flightStartDate)} – ${
                          campaign.flightEndDate ? formatShortDate(campaign.flightEndDate) : '…'
                        }`
                      : '—'}
                  </DatatableCell>
                  {/* Null means the platform never reported one - an em dash, not a guess. */}
                  <DatatableCell>{campaign.currency ?? '—'}</DatatableCell>
                  <DatatableCell>
                    <Select
                      value={campaign.role}
                      onValueChange={(next) => handleRoleChange(campaign, next)}
                    >
                      <SelectTrigger
                        aria-label={t('projectDetail.manageCampaigns.roleForCampaign', {
                          name: campaign.name,
                        })}
                        size="sm"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* eslint-disable-next-line i18next/no-literal-string -- CampaignRole wire enum */}
                        <SelectItem value="buying">
                          {t('projectDetail.manageCampaigns.buying')}
                        </SelectItem>
                        <SelectItem value="ad_serving">
                          {t('projectDetail.manageCampaigns.adServing')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </DatatableCell>
                  <DatatableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('projectDetail.manageCampaigns.removeCampaign', {
                        name: campaign.name,
                      })}
                      onClick={() => setToRemove(campaign)}
                    >
                      {t('projectDetail.manageCampaigns.remove')}
                    </Button>
                  </DatatableCell>
                </DatatableRow>
              ))}
            </DatatableBody>
          </Datatable>
        )}

        {/* Mounted only while open so each add starts on a clean form: the connector
          choice and every list it drives read fresh instead of carrying over from the
          last time this was opened. */}
        {isAddOpen && (
          <AddCampaignDialog
            projectId={projectId}
            open={isAddOpen}
            onClose={() => setIsAddOpen(false)}
          />
        )}
      </SettledContent>

      {/* Mounted only while open, so each upload starts from the file step. */}
      {isUploadOpen && (
        <DeliveryFileUploadDialog
          projectId={projectId}
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
        />
      )}

      <AlertDialog
        open={toRemove !== null}
        onOpenChange={(open) => {
          if (!open) setToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('projectDetail.manageCampaigns.removeFromThisProject', {
                name: toRemove?.name ?? '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('projectDetail.manageCampaigns.itStopsContributingToTheOverview')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('projectDetail.manageCampaigns.keepIt')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={unlink.isPending}
              onClick={handleConfirmRemove}
            >
              {unlink.isPending
                ? t('projectDetail.manageCampaigns.removing')
                : t('projectDetail.manageCampaigns.removeFromProject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
